import { products } from "@/content/catalog";
import { knowledgeArticles, productNameEn, t } from "@/content/site-content";
import { switchLocalePath, type ResolvedRoute } from "@/content/routes";

export const SITE_ORIGIN = "https://living1991.pages.dev";

export interface RouteMeta {
  title: string;
  description: string;
  canonical: string;
  alternates: { hreflang: string; href: string }[];
  noIndex: boolean;
  h1: string;
  jsonLd: Record<string, unknown>[];
}

const pageNames = {
  home: { "zh-TW": "我們的未來，從餐桌開始", en: "Our future starts from the dining table" },
  products: { "zh-TW": "選購食器", en: "Shop tableware" },
  business: { "zh-TW": "企業採購與循環合作", en: "Business procurement and circular partnerships" },
  industries: { "zh-TW": "產業應用", en: "Industry applications" },
  industry: { "zh-TW": "產業應用", en: "Industry application" },
  oem: { "zh-TW": "OEM／ODM／OBM 九步合作流程", en: "Nine-step OEM / ODM / OBM process" },
  recycling: { "zh-TW": "封閉式循環回收方案", en: "Closed-loop recycling programme" },
  quote: { "zh-TW": "企業大宗採購詢價", en: "Business enquiry" },
  material: { "zh-TW": "永續材質", en: "Sustainable materials" },
  certifications: { "zh-TW": "認證與檢測", en: "Certifications and test documents" },
  about: { "zh-TW": "關於哩皿", en: "About Living Forward" },
  knowledge: { "zh-TW": "永續知識", en: "Sustainability knowledge" },
  "knowledge-category": { "zh-TW": "永續知識分類", en: "Sustainability knowledge category" },
  article: { "zh-TW": "永續知識", en: "Sustainability knowledge" },
  faq: { "zh-TW": "常見問題", en: "Questions and answers" },
  contact: { "zh-TW": "聯絡我們", en: "Contact" },
  search: { "zh-TW": "搜尋與 AI 搜尋", en: "Search and AI search" },
  account: { "zh-TW": "會員中心", en: "Member account" },
  cart: { "zh-TW": "購物車", en: "Cart" },
  checkout: { "zh-TW": "結帳", en: "Checkout" },
  "checkout-result": { "zh-TW": "結帳結果", en: "Checkout result" },
  policy: { "zh-TW": "網站政策", en: "Site policy" },
  states: { "zh-TW": "介面狀態", en: "Interface states" },
  "not-found": { "zh-TW": "找不到頁面", en: "Page not found" },
  product: { "zh-TW": "商品詳情", en: "Product detail" },
} as const;

function localizedProductName(productId: string, locale: ResolvedRoute["locale"]): string {
  const product = products.find((item) => item.id === productId);
  if (!product) return t(pageNames.product, locale);
  return locale === "en" ? productNameEn[product.id] ?? product.name : product.name;
}

function routeH1(route: ResolvedRoute): string {
  if (route.kind === "product") {
    const product = products.find((item) => item.slug === route.slug);
    return product ? localizedProductName(product.id, route.locale) : t(pageNames.product, route.locale);
  }
  if (route.kind === "article") {
    const article = knowledgeArticles.find((item) => item.slug === route.slug);
    return article ? t(article.title, route.locale) : t(pageNames.article, route.locale);
  }
  return t(pageNames[route.kind], route.locale);
}

function breadcrumbJsonLd(route: ResolvedRoute, h1: string) {
  const homePath = route.locale === "en" ? "/en" : "/zh-tw";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: route.locale === "en" ? "Home" : "首頁",
        item: `${SITE_ORIGIN}${homePath}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: h1,
        item: `${SITE_ORIGIN}${route.path === "/" ? homePath : route.path}`,
      },
    ],
  };
}

function routeJsonLd(route: ResolvedRoute, h1: string): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = [breadcrumbJsonLd(route, h1)];
  if (route.kind === "home") {
    result.push({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "哩皿食器",
      url: `${SITE_ORIGIN}${route.locale === "en" ? "/en" : "/zh-tw"}`,
      logo: `${SITE_ORIGIN}/assets/official/brand-logo.webp`,
      description:
        route.locale === "en"
          ? "Reusable tableware, business procurement, and circular partnership information."
          : "整合消費者購物、企業採購與循環合作的哩皿食器官方網站 DEMO。",
    });
  }
  if (route.kind === "product") {
    const product = products.find((item) => item.slug === route.slug);
    if (product) {
      const productLd: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: localizedProductName(product.id, route.locale),
        sku: product.sku,
        material: product.material,
        image: product.image ? `${SITE_ORIGIN}${product.image}` : undefined,
        description: product.shortDescription,
      };
      if (product.price) {
        productLd.offers = {
          "@type": "Offer",
          priceCurrency: product.price.currency,
          price: product.price.amount,
          availability:
            product.inventory.status === "in_stock"
              ? "https://schema.org/InStock"
              : "https://schema.org/PreOrder",
          url: `${SITE_ORIGIN}${route.path}`,
        };
      }
      result.push(productLd);
    }
  }
  if (route.kind === "article") {
    const article = knowledgeArticles.find((item) => item.slug === route.slug);
    if (article) {
      result.push({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: t(article.title, route.locale),
        description: t(article.summary, route.locale),
        dateModified: article.checkedAt,
        mainEntityOfPage: `${SITE_ORIGIN}${route.path}`,
        publisher: { "@type": "Organization", name: "哩皿食器" },
      });
    }
  }
  return result;
}

export function getRouteMeta(route: ResolvedRoute): RouteMeta {
  const h1 = routeH1(route);
  const localeLabel = route.locale === "en" ? "Living Forward" : "哩皿食器";
  const canonicalPath = route.path === "/" ? "/zh-tw" : route.path;
  const description =
    route.kind === "product"
      ? `${h1}｜${route.locale === "en" ? "official-source specifications, structured DEMO price, and business enquiry." : "查看官方來源規格、結構化 DEMO 價格與企業大宗詢價入口。"}`
      : route.kind === "article"
        ? t(
            knowledgeArticles.find((item) => item.slug === route.slug)?.summary ?? {
              "zh-TW": "以官方來源整理的永續食器知識。",
              en: "Official-source sustainability knowledge.",
            },
            route.locale,
          )
        : route.locale === "en"
          ? `${h1}. Explore reusable tableware, business procurement, and circular partnerships.`
          : `${h1}。整合可重複使用食器、企業採購、材質知識與循環合作。`;
  return {
    title: `${h1}｜${localeLabel}`,
    description,
    canonical: `${SITE_ORIGIN}${canonicalPath}`,
    alternates: [
      {
        hreflang: "zh-TW",
        href: `${SITE_ORIGIN}${switchLocalePath(canonicalPath, "zh-TW")}`,
      },
      {
        hreflang: "en",
        href: `${SITE_ORIGIN}${switchLocalePath(canonicalPath, "en")}`,
      },
      {
        hreflang: "x-default",
        href: `${SITE_ORIGIN}${switchLocalePath(canonicalPath, "zh-TW")}`,
      },
    ],
    noIndex: Boolean(route.noIndex),
    h1,
    jsonLd: routeJsonLd(route, h1),
  };
}
